import { flexRender, Table } from '@tanstack/react-table';
import styled from 'styled-components';
import { Palette, themeConfig } from '@/util/themeConfig';

type DesktopLayoutProps<TData> = {
  table: Table<TData>;
  layout: 'auto' | 'fixed';
  colors: Palette;
};

function DesktopLayout<TData>({
  table,
  layout,
  colors,
}: DesktopLayoutProps<TData>) {
  return (
    <StyledTable layout={layout} colors={colors}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <StyledTableHeaderCell
                key={header.id}
                align={header.column.columnDef.meta?.align}
                width={header.column.getSize()}
                colors={colors}
              >
                {header.isPlaceholder
                  ? null
                  : (header.column.columnDef.header as string)}
              </StyledTableHeaderCell>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <StyledTableCell
                key={cell.id}
                align={cell.column.columnDef.meta?.align}
                width={cell.column.getSize()}
                colors={colors}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </StyledTableCell>
            ))}
          </tr>
        ))}
      </tbody>
    </StyledTable>
  );
}

const StyledTable = styled.table<{
  layout: 'auto' | 'fixed';
  colors: Palette;
}>`
  width: 100%;
  border-collapse: collapse;
  color: ${(props) => props.colors.text};
  background-color: ${(props) => props.colors.layoutBg};
  table-layout: ${(props) => props.layout};
`;

const StyledTableHeaderCell = styled.th<{
  align?: 'left' | 'center' | 'right';
  width?: number;
  colors: Palette;
}>`
  padding: 12px 16px;
  color: ${(props) => props.colors.text};
  background-color: ${(props) => props.colors.layoutOverlayBg};
  border: 1px solid ${(props) => props.colors.borderHover};
  text-align: ${(props) => props.align || 'left'};
  width: ${(props) => (props.width ? `${props.width}px` : 'auto')};
  font-weight: bold;
`;

const StyledTableCell = styled.td<{
  align?: 'left' | 'center' | 'right';
  width?: number;
  colors: Palette;
}>`
  padding: 12px 16px;
  border: 1px solid ${(props) => props.colors.border};
  text-align: ${(props) => props.align || 'left'};
  width: ${(props) => (props.width ? `${props.width}px` : 'auto')};
`;

export default DesktopLayout;
